import React, { Fragment, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Transition } from '@headlessui/react';

import GridBackground from 'shared/components/ui/GridBackground/GridBackground';
import { useShowSignModal } from 'shared/hooks/useShowSignModal';

import './Modal.scss';

interface Props {
  open: boolean;
  children: React.ReactNode;
  className?: string;
  childrenClassName?: string;
  withGridBackground?: boolean;
}

interface Props {
  open: boolean;
  children: React.ReactNode;
  className?: string; // overlay
  childrenClassName?: string; // OUTER panel (size, border, etc.)
  contentClassName?: string; // INNER content wrapper (padding, layout)
  withGridBackground?: boolean;
}

const ModalWithFullScreenBg = ({
  open,
  children,
  className = '',
  childrenClassName = '',
  contentClassName = '',
  withGridBackground = true,
}: Props) => {
  const { showSignModal } = useShowSignModal();

  return createPortal(
    <Transition appear show={open && !showSignModal} as={Fragment}>
      <div className={`fixed inset-0 z-50 ${className}`}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Scroll container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* PANEL (this is the one you usually want to style) */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.98]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.98]"
            >
              <div
                className={[
                  'relative w-full',
                  'max-h-[calc(100vh-2rem)] overflow-y-auto',
                  'rounded-[24px]',
                  childrenClassName || 'max-w-[610px] border-[2px] border-border sm:border-[3px]',
                ].join(' ')}
              >
                {withGridBackground ? (
                  <GridBackground className="rounded-[24px]">
                    <div className={`rounded-[24px] ${contentClassName}`}>{children}</div>
                  </GridBackground>
                ) : contentClassName ? (
                  <div className={`rounded-[24px] ${contentClassName}`}>{children}</div>
                ) : (
                  children // no extra inner div if not needed
                )}
              </div>
            </Transition.Child>
          </div>
        </div>
      </div>
    </Transition>,
    document.body,
  );
};

export default ModalWithFullScreenBg;
